import React, { useState, useEffect } from "react";
import { Flex, Text, Card, Grid, Button, IconButton, Tooltip, Dialog, TextField, Select, Progress, Badge, Separator, AlertDialog, Box, Switch } from "@radix-ui/themes";
import { PlusIcon, TrashIcon, CopyIcon, DownloadIcon, ClockIcon } from "@radix-ui/react-icons";
import { ToastAlert } from "components";
import { useProfileContext, useLocationContext } from "context";
import { useNavigate } from "react-router-dom";
import { useGetAllBrands } from "hooks";
import { useSurveyTemplates } from "hooks/bSurveys/useSurveyTemplates";
import { useTheme } from "../../context/ThemeContext";
import { ReactComponent as LightQrCodeIcon } from "assets/icons/qr_code.svg";
import { ReactComponent as DarkQrCodeIcon } from "assets/icons/dark_mode_qr_code.svg";
import { useToast } from "../../context/ToastContext";

const colorConversion = (inputColour, opacity) => {
  if (!inputColour || opacity === undefined) return "rgba(0, 0, 0, 0)";
  let hex = inputColour.replace("#", "");
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
};

// Mini preview of the survey intro page, rendered at scale for template cards
const SurveyTemplatePreview = ({ template }) => {
  const primaryColor = template.primary_color || '#0066cc';
  const textColor = template.text_color || '#ffffff';
  const introTitle = template.intro_title || 'Share Your Experience';
  const introSubtitle = template.intro_subtitle || '';
  const bgImageUrl = template.background_image_url;
  const logoUrl = template.logo_url;
  const overlayColor = template.background_overlay_color || '#000000';
  const overlayOpacity = template.background_overlay_opacity ?? 50;
  const blurAmount = template.background_blur_amount ?? 0;

  return (
    <Box style={{ 
      width: '100%', 
      height: '100%', 
      overflow: 'hidden',
      position: 'relative',
      background: !bgImageUrl 
        ? `linear-gradient(135deg, ${primaryColor}22 0%, ${primaryColor}44 100%)`
        : undefined,
    }}>
      {/* Background with blur */}
      {bgImageUrl && (
        <>
          <div style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${bgImageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: `blur(${(blurAmount / 100) * 20}px)`,
            transform: blurAmount > 0 ? 'scale(1.1)' : undefined,
            zIndex: 0,
          }} />
          <div style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: colorConversion(overlayColor, overlayOpacity),
            zIndex: 1,
          }} />
        </>
      )}
      {/* Content */}
      <Flex
        direction="column"
        align="center"
        justify="center"
        gap="2"
        style={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          height: '100%',
          padding: '12px 16px',
          textAlign: 'center',
          pointerEvents: 'none',
        }}
      >
        {/* Logo */}
        {logoUrl && (
          <img 
            src={logoUrl} 
            alt="" 
            style={{ 
              maxWidth: '48px', 
              maxHeight: '48px', 
              objectFit: 'contain',
              borderRadius: '4px',
            }}
          />
        )}
        {/* Title */}
        <Text size="2" weight="bold" style={{ color: textColor, lineHeight: 1.2 }}>
          {introTitle}
        </Text>
        {/* Subtitle */}
        {introSubtitle ? (
          <Text size="1" style={{ color: textColor, opacity: 0.85, lineHeight: 1.2 }}>
            {introSubtitle}
          </Text>
        ) : null}
        {/* Time estimate */}
        <Flex align="center" gap="1" style={{ opacity: 0.6 }}>
          <ClockIcon width={10} height={10} style={{ color: textColor }} />
          <Text style={{ color: textColor, fontSize: '9px' }}>{template.intro_time_estimate}</Text>
        </Flex>
        {/* Begin button */}
        <div style={{
          marginTop: '2px',
          padding: '3px 16px',
          borderRadius: '6px',
          backgroundColor: primaryColor,
          color: '#fff',
          fontSize: '10px',
          fontWeight: 600,
        }}>
          Begin
        </div>
      </Flex>
    </Box>
  );
};

const getPublicSurveyBaseUrl = () => {
  const configuredBaseUrl = process.env.REACT_APP_BSURVEY_PUBLIC_URL;
  if (configuredBaseUrl && configuredBaseUrl.trim()) {
    return configuredBaseUrl.trim().replace(/\/+$/, "");
  }
  return window.location.origin.replace(/\/+$/, "");
};

const getPublicSurveyLink = (templateId) => `${getPublicSurveyBaseUrl()}/surveys/${templateId}`;

const getQrCodeUrl = (data, color = "666666", format = "png") => (
  `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(data)}&size=220x220&bgcolor=ffffff&color=${color}&format=${format}`
);

export default function SurveyTemplates() {
  const { userProfile } = useProfileContext();
  const { locationsData } = useLocationContext();
  const { appearance } = useTheme();
  const { setToastMessage } = useToast();
  const navigate = useNavigate();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ name: "", assignedTo: "unspecified" });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [templateForQr, setTemplateForQr] = useState(null);

  // Fetch all brands for this organisation
  const { data: brandsData, isLoading: isLoadingBrands } = useGetAllBrands(userProfile.organisation_id);
  
  // Fetch survey templates from API
  const { 
    surveyTemplates, 
    isLoading, 
    error, 
    createTemplateAsync,
    isCreating,
    updateTemplate,
    deleteTemplate, 
    isDeleting 
  } = useSurveyTemplates(userProfile.organisation_id);

  // State to hold filtered data
  const [brands, setBrands] = useState([]);
  const [independentLocations, setIndependentLocations] = useState([]);

  // Process the data when it's loaded (same pattern as GuestPortalSelector)
  useEffect(() => {
    if (brandsData && locationsData) {
      // Find the default brand
      const defaultBrandItem = brandsData.find((brand) => brand.is_default);

      // Filter out the default brand and get non-default brands
      const nonDefaultBrands = brandsData.filter((brand) => !brand.is_default);
      setBrands(nonDefaultBrands);

      // Filter locations that use the default brand
      const defaultLocations = locationsData.filter((location) => location.brand_id === defaultBrandItem?.id || location.brand_id === null);
      setIndependentLocations(defaultLocations);
    }
  }, [brandsData, locationsData]);

  // Use API data (surveyTemplates) or empty array
  const templates = surveyTemplates || [];
  const hasTemplates = templates.length > 0;
  const activeTemplates = templates.filter(t => t.is_active);
  const inactiveTemplates = templates.filter(t => !t.is_active);
  const QrCodeIcon = appearance === "dark" ? DarkQrCodeIcon : LightQrCodeIcon;

  const handleCreateTemplate = async () => {
    try {
      // Create the template in the database immediately
      const payload = {
        name: newTemplate.name,
        assigned_to: newTemplate.assignedTo,
        organisation_id: userProfile.organisation_id,
        // Default form blocks will be created by the backend (email block auto-created)
        form_blocks: [
          {
            block_type: 'experienceRating',
            question: 'How would you rate your overall experience?',
            is_required: true,
            is_locked: false,
            order: 1,
            options: [],
          },
          {
            block_type: 'howDidYouFindUs',
            question: 'How did you hear about us?',
            is_required: false,
            is_locked: false,
            order: 2,
            options: [
              { text: 'Google', order: 0 },
              { text: 'Facebook', order: 1 },
              { text: 'Instagram', order: 2 },
              { text: 'Friend/Family', order: 3 },
              { text: 'Walk-by', order: 4 },
              { text: 'Other', order: 5 },
            ],
          },
        ],
      };

      const response = await createTemplateAsync(payload);
      const createdTemplate = response.data || response;
      
      // Navigate to editor with the created template's UUID
      navigate(`/surveys/templates/editor/${createdTemplate.template_id}`, {
        state: { template: createdTemplate }
      });
      
      setIsCreateDialogOpen(false);
      setNewTemplate({ name: "", assignedTo: "unspecified" });
    } catch (error) {
      setToastMessage('Failed to create template. Please try again.');
    }
  };

  const handleOpenEditor = (template) => {
    // Navigate to template editor with the template UUID
    navigate(`/surveys/templates/editor/${template.template_id}`, {
      state: { template }
    });
  };

  const handleDeleteTemplate = (e, template) => {
    e.stopPropagation();
    setTemplateToDelete(template);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (templateToDelete) {
      deleteTemplate(templateToDelete.template_id);
    }
    setDeleteConfirmOpen(false);
    setTemplateToDelete(null);
  };

  const handleOpenQrDialog = (e, template) => {
    e.stopPropagation();
    setTemplateForQr(template);
    setQrDialogOpen(true);
  };

  const handleToggleActive = (e, template) => {
    e.stopPropagation();
    updateTemplate(
      { templateId: template.template_id, data: { is_active: !template.is_active } },
      {
        onSuccess: () => {
          setToastMessage(template.is_active ? 'Survey deactivated' : 'Survey activated');
        },
      }
    );
  };

  const handleDownloadQr = async () => {
    if (!templateForQr?.template_id) return;
    const link = getPublicSurveyLink(templateForQr.template_id);
    const qrCodeUrl = getQrCodeUrl(link);
    try {
      const response = await fetch(qrCodeUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${templateForQr.name || "survey"}-qr-code.png`;
      document.body.appendChild(anchor);
      anchor.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(anchor);
      setToastMessage("QR code downloaded successfully");
    } catch (error) {
      setToastMessage("Failed to download QR code. Please try again.");
    }
  };

  const handleCopySurveyLink = async () => {
    if (!templateForQr?.template_id) return;
    try {
      await navigator.clipboard.writeText(getPublicSurveyLink(templateForQr.template_id));
      setToastMessage("Link copied to clipboard");
    } catch (error) {
      setToastMessage("Failed to copy survey link.");
    }
  };

  const handleCopyQrCode = async () => {
    if (!templateForQr?.template_id) return;
    const qrCodeUrl = getQrCodeUrl(getPublicSurveyLink(templateForQr.template_id));
    try {
      const response = await fetch(qrCodeUrl);
      const blob = await response.blob();
      await navigator.clipboard.write([
        new ClipboardItem({ "image/png": blob }),
      ]);
      setToastMessage("QR code copied to clipboard");
    } catch (error) {
      setToastMessage("Failed to copy QR code.");
    }
  };

  // Helper to get assignment label for display
  const getAssignmentLabel = (assignedTo) => {
    if (!assignedTo || assignedTo === "unspecified") return "General";
    if (assignedTo.startsWith("brand-")) {
      const brandId = assignedTo.replace("brand-", "");
      const brand = brands.find(b => String(b.id) === brandId);
      return brand ? brand.name : "Brand";
    }
    if (assignedTo.startsWith("location-")) {
      const locationId = assignedTo.replace("location-", "");
      const location = independentLocations.find(l => String(l.id) === locationId);
      return location ? location.name : "Location";
    }
    return "General";
  };

  const renderTemplateCard = (template) => (
    <Card asChild
      key={template.id} 
      onClick={() => handleOpenEditor(template)} 
      style={{ display: "flex", flexDirection: "column", padding: '0', cursor: 'pointer', overflow: 'hidden', height: '220px' }}>
      <button>
        {/* Preview area */}
        <Flex 
          style={{ 
            flex: 1, 
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <SurveyTemplatePreview template={template} />
          <Badge 
            color={template.assigned_to === "unspecified" ? "gray" : "blue"} 
            variant="solid" 
            size="1" 
            style={{ position: 'absolute', top: 8, left: 8, zIndex: 3 }}
          >
            {getAssignmentLabel(template.assigned_to)}
          </Badge>
        </Flex>
        <Separator size="4" orientation="horizontal" style={{ minHeight: '1px' }} />
        {/* Info area */}
        <Flex p="3" direction="column" gap="1" style={{ width: "100%" }}>
          <Flex justify="between" align="center">
            <Text weight="medium" size="2">{template.name}</Text>
            <Flex align="center" gap="2">
              <Tooltip content={template.is_active ? 'Deactivate survey' : 'Activate survey'}>
                <Box 
                  onClick={(e) => handleToggleActive(e, template)}
                  style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                >
                  <Switch 
                    size="1"
                    checked={template.is_active}
                    color="grass"
                    onCheckedChange={() => {}}
                    tabIndex={-1}
                    style={{ pointerEvents: 'none' }}
                  />
                </Box>
              </Tooltip>
              <Tooltip content="Public survey link & QR">
                <IconButton
                  size="1"
                  variant="ghost"
                  color="gray"
                  onClick={(e) => handleOpenQrDialog(e, template)}
                >
                  <QrCodeIcon style={{ width: 14, height: 14 }} />
                </IconButton>
              </Tooltip>
              <Tooltip content="Delete template">
                <IconButton 
                  size="1" 
                  variant="ghost" 
                  color="red"
                  onClick={(e) => handleDeleteTemplate(e, template)}
                  disabled={isDeleting}
                >
                  <TrashIcon />
                </IconButton>
              </Tooltip>
            </Flex>
          </Flex>
          <Flex gap="3">
            <Text size="1" color="gray">{template.blocks_count || 0} blocks</Text>
          </Flex>
        </Flex>
      </button>
    </Card>
  );

  return (
    <>
      {/* Loading Progress Bar */}
      {isLoading && (
        <div style={{ 
          position: "fixed", 
          top: 0, 
          left: 0, 
          width: "100%", 
          zIndex: 1000 
        }}>
          <Progress size="1" duration="1" />
        </div>
      )}
      
      <Flex direction="column" gap="5" style={{ alignSelf: "stretch", alignItems: "center", flex: "1 0 0" }}>
        {/* Header */}
        <Flex justify="between" align="center" style={{ alignSelf: "stretch" }}>
          <Text size="3" weight="medium">Survey templates</Text>
          {/* Create Template Button */}
          <Dialog.Root open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <Dialog.Trigger>
              <Button variant="soft">
                <PlusIcon width="16" height="16" />
                Create template
              </Button>
            </Dialog.Trigger>
            <Dialog.Content style={{ maxWidth: 450 }}>
              <Dialog.Title>Create Survey Template</Dialog.Title>
              <Dialog.Description size="2" mb="4" color="gray">
                Create a new survey template with customizable form blocks.
              </Dialog.Description>
              
              <Flex direction="column" gap="3">
                <label>
                  <Text as="div" size="2" mb="1" weight="medium">
                    Template name
                  </Text>
                  <TextField.Root
                    placeholder="Enter template name"
                    value={newTemplate.name}
                    onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                  />
                </label>
                <label>
                  <Text as="div" size="2" mb="1" weight="medium">
                    Assigned to
                  </Text>
                  <Select.Root 
                    value={newTemplate.assignedTo} 
                    onValueChange={(value) => setNewTemplate({ ...newTemplate, assignedTo: value })}
                  >
                    <Select.Trigger placeholder="Select brand or location" style={{ width: "100%" }} />
                    <Select.Content>
                      <Select.Group>
                        <Select.Label>General</Select.Label>
                        <Select.Item value="unspecified">Unspecified</Select.Item>
                      </Select.Group>
                      {brands.length > 0 && (
                        <Select.Group>
                          <Select.Label>Brands</Select.Label>
                          {brands.map((brand) => (
                            <Select.Item key={`brand-${brand.id}`} value={`brand-${brand.id}`}>
                              {brand.name}
                            </Select.Item>
                          ))}
                        </Select.Group>
                      )}
                      {independentLocations.length > 0 && (
                        <Select.Group>
                          <Select.Label>Locations</Select.Label>
                          {independentLocations.map((location) => (
                            <Select.Item key={`location-${location.id}`} value={`location-${location.id}`}>
                              {location.name}
                            </Select.Item>
                          ))}
                        </Select.Group>
                      )}
                    </Select.Content>
                  </Select.Root>
                </label>
              </Flex>

              <Flex gap="3" mt="4" justify="end">
                <Dialog.Close>
                  <Button variant="soft" color="gray" disabled={isCreating}>
                    Cancel
                  </Button>
                </Dialog.Close>
                <Button 
                  onClick={handleCreateTemplate}
                  disabled={!newTemplate.name.trim() || isCreating}
                >
                  {isCreating ? 'Creating...' : 'Create template'}
                </Button>
              </Flex>
            </Dialog.Content>
          </Dialog.Root>
        </Flex>

        {/* Template cards */}
        <Flex direction="column" gap="5" style={{ alignSelf: "stretch", width: "100%", overflow: 'auto', height: '100%',
          flex: "1 0 0" }}>
          { !hasTemplates ? (
            <Flex direction="column" style={{justifyContent: 'center', alignItems: 'center', alignSelf: 'stretch',
              border: '1px dashed var(--gray-a6)', borderRadius: 'var(--radius-4)', height: '100%', padding: 'var(--space-6)'}}>
              <Text color="gray" size="3">No survey templates yet.</Text>
              <Text color="gray" size="2" style={{ marginTop: 'var(--space-2)' }}>
                Create your first survey template to start collecting customer feedback.
              </Text>
            </Flex>
          ) : (
            <Flex direction="column" gap="5">
              {/* Active Surveys Section */}
              {activeTemplates.length > 0 && (
                <Flex direction="column" gap="3">
                  <Flex align="center" gap="2">
                    <Box style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'var(--grass-9)', flexShrink: 0 }} />
                    <Text size="2" weight="medium" color="gray">Active surveys ({activeTemplates.length})</Text>
                  </Flex>
                  <Grid columns="3" gap="4" style={{ alignSelf: "stretch", width: "100%" }}>
                    {activeTemplates.map((template) => renderTemplateCard(template))}
                  </Grid>
                </Flex>
              )}

              {/* Inactive Surveys Section */}
              {inactiveTemplates.length > 0 && (
                <Flex direction="column" gap="3">
                  <Flex align="center" gap="2">
                    <Box style={{ width: 8, height: 8, borderRadius: '50%', border: '2px solid var(--gray-8)', flexShrink: 0 }} />
                    <Text size="2" weight="medium" color="gray">Inactive surveys ({inactiveTemplates.length})</Text>
                  </Flex>
                  <Grid columns="3" gap="4" style={{ alignSelf: "stretch", width: "100%" }}>
                    {inactiveTemplates.map((template) => renderTemplateCard(template))}
                  </Grid>
                </Flex>
              )}
            </Flex>
          )}
        </Flex>
      </Flex>

      {/* Delete Confirmation Dialog */}
      <AlertDialog.Root open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialog.Content maxWidth="450px">
          <AlertDialog.Title>Delete Survey Template</AlertDialog.Title>
          <AlertDialog.Description size="2">
            Are you sure you want to delete "{templateToDelete?.name}"? This action cannot be undone and all associated responses will be lost.
          </AlertDialog.Description>
          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button variant="solid" color="red" onClick={confirmDelete} disabled={isDeleting}>
                {isDeleting ? 'Deleting...' : 'Delete template'}
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>

      <Dialog.Root
        open={qrDialogOpen}
        onOpenChange={(open) => {
          setQrDialogOpen(open);
          if (!open) {
            setTemplateForQr(null);
          }
        }}
      >
        <Dialog.Content style={{ maxWidth: 500 }}>
          <Dialog.Title>Survey Link & QR Code</Dialog.Title>
          <Dialog.Description size="2" mb="3" color="gray">
            Share this QR code or link to open the public survey page.
          </Dialog.Description>

          <Flex direction="column" gap="3">
            <Text size="2" weight="medium">Public Survey Link</Text>
            <Flex
              align="center"
              gap="2"
              style={{
                backgroundColor: "var(--gray-a3)",
                padding: "var(--space-2) var(--space-3)",
                borderRadius: "var(--radius-2)",
              }}
            >
              <Text size="2" style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {templateForQr?.template_id ? getPublicSurveyLink(templateForQr.template_id) : ""}
              </Text>
              <Tooltip content="Copy link">
                <IconButton size="1" variant="ghost" onClick={handleCopySurveyLink} disabled={!templateForQr?.template_id}>
                  <CopyIcon />
                </IconButton>
              </Tooltip>
            </Flex>

            <Separator size="4" />

            <Text size="2" weight="medium">QR Code</Text>
            <Flex justify="center" p="4" style={{ backgroundColor: "var(--gray-a2)", borderRadius: "var(--radius-3)" }}>
              {templateForQr?.template_id ? (
                <img
                  src={getQrCodeUrl(getPublicSurveyLink(templateForQr.template_id))}
                  alt="Survey QR Code"
                  style={{ width: 220, height: 220, borderRadius: "var(--radius-2)" }}
                />
              ) : null}
            </Flex>
          </Flex>

          <Flex justify="between" mt="4" gap="3">
            <Button variant="soft" color="gray" onClick={() => setQrDialogOpen(false)}>
              Close
            </Button>
            <Flex gap="2">
              <Button variant="soft" color="gray" onClick={handleCopyQrCode} disabled={!templateForQr?.template_id}>
                <CopyIcon /> Copy QR
              </Button>
              <Button variant="solid" onClick={handleDownloadQr} disabled={!templateForQr?.template_id}>
                <DownloadIcon /> Download QR
              </Button>
            </Flex>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
      <ToastAlert />
    </>
  );
}
